import { isEmpty, isNil } from 'lodash';
import {
  selectMap3dDataset,
  selectMap3dWorkspace,
} from 'map-3d/shared/map-3d-slices';
import { useMemo } from 'react';
import { useMyOrg } from 'shared/api';
import { CustomDate } from 'shared/utils';
import { DateTimeFormatLength } from 'shared/utils/enums';
import { useAppSelector } from 'src/app/hooks';
import { Image } from 'src/assets';
import { getCesiumScalePrintStyle } from '../../helpers';

type PrintHeaderProps = {
  reportTitle: string;
};

export const PrintHeader: React.FC<PrintHeaderProps> = ({ reportTitle }) => {
  // Selectors.
  const { selectedProject, selectedTerrainIteration, selectedTerrainSite } =
    useAppSelector(selectMap3dDataset);
  const { selectedPointCloudLayerId, workspaceLayers } =
    useAppSelector(selectMap3dWorkspace);

  // APIs.
  const { data: myOrgResponse } = useMyOrg();

  // Booleans.
  const hasPointCloudLayer = !isNil(selectedPointCloudLayerId);

  // Memoized values.
  const selectedPointCloudLayer = useMemo(() => {
    return hasPointCloudLayer
      ? (workspaceLayers[selectedPointCloudLayerId] ?? null)
      : null;
  }, [workspaceLayers, selectedPointCloudLayerId]);

  const selectedDataset = useMemo(
    () => ({
      project: selectedPointCloudLayer?.project ?? selectedProject,
      site: selectedPointCloudLayer?.site ?? selectedTerrainSite,
      iteration: selectedPointCloudLayer?.iteration ?? selectedTerrainIteration,
    }),
    [
      selectedPointCloudLayer,
      selectedProject,
      selectedTerrainSite,
      selectedTerrainIteration,
    ],
  );

  const generatedTime = new CustomDate(Date.now()).formatDateTime(
    DateTimeFormatLength.Long,
    DateTimeFormatLength.Medium,
  );

  const logo = myOrgResponse?.data.logo;
  const orgLogo = isEmpty(logo) || isNil(logo) ? Image.DefaultOrgLogo : logo;

  const formattedIterationDate =
    selectedDataset.iteration?.date?.formatDate(DateTimeFormatLength.Long) ??
    null;

  return (
    <div className="print-header">
      <style>{getCesiumScalePrintStyle()}</style>
      <div className="print-header--main">
        <div className="print-header__report-title">{reportTitle}</div>
        <div className="print-header__captured-time">
          <span className="print-header__captured-time__header">
            Generated on
          </span>
          <span>{generatedTime}</span>
        </div>
      </div>
      <div className="print-header--sub">
        <div className="print-header__org">
          <img
            src={orgLogo}
            alt="Organization logo"
            className="print-header__org__logo"
          />
          <span className="print-header__org__name">
            {myOrgResponse?.data.name}
          </span>
        </div>
        <div className="print-header__dataset--project">
          <div className="print-header__dataset__sub-header">Project</div>
          <div className="print-header__dataset__value">
            {selectedDataset.project?.name}
          </div>
        </div>
        <div className="print-header__dataset--site">
          <div className="print-header__dataset__sub-header">Site</div>
          <div className="print-header__dataset__value">
            {selectedDataset.site?.name}
          </div>
        </div>
        <div className="print-header__dataset--iteration">
          <div className="print-header__dataset__sub-header">Iteration</div>
          <div className="print-header__dataset__value">
            {!isNil(selectedDataset.iteration) && (
              <>
                <span className="iteration-name">
                  {selectedDataset.iteration?.name}
                </span>
                {formattedIterationDate && (
                  <span className="iteration-date">
                    Survey Date: {formattedIterationDate}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        {hasPointCloudLayer && (
          <div className="print-header__dataset--point-cloud-layer">
            <div className="print-header__dataset__sub-header">
              Point Cloud Layer
            </div>
            <div className="print-header__dataset__value">
              {selectedPointCloudLayer?.name}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
